import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAssetsByUserToken, getAssetById, removeAsset } from "shared/api/client/orvalHelpers";
import ImageWithSpinner from "shared/components/ui/ImageWithSpinner/ImageWithSpinner";
import { Tooltip } from "react-tooltip";
import ReactPaginate from "react-paginate";
import { getCookie } from "shared/auth/utils/cookieUtils";
import EmailVerificationBanner from "features/account/components/EmailVerificationBanner";
import { assetApi, apiClient } from "shared/api/client/orval";
import { parseApiError } from "shared/api/client/errorParser";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import { useTranscodeStatus } from "features/creator-upload/hooks/useTranscodeStatus";

const TRANSCODING_VIDEO_STATUS = /^(queued|processing|encoding|transcoding)$/i;

const getMainVideo = (item: any) =>
  item?.videoLib?.find((v: any) => v?.role === "MainVideo") ??
  item?.videoLib?.[0];

const isTitleTranscoding = (t: { status: string; videoStatus: string }) =>
  t.status === "Encoding" || TRANSCODING_VIDEO_STATUS.test(t.videoStatus);

const TitleTranscodeStatus: React.FC<{ videoId: string; onCompleted: () => void }> = ({ videoId, onCompleted }) => {
  const { status, unavailable } = useTranscodeStatus(videoId || undefined);
  const completed = useRef(false);
  useEffect(() => { completed.current = false; }, [videoId]);
  useEffect(() => {
    if (status?.state === "completed" && !completed.current) {
      completed.current = true;
      onCompleted();
    }
  }, [status?.state, onCompleted]);

  let label = "Encoding video…";
  if (status && !unavailable) {
    if (status.state === "completed") label = "Encoding complete";
    else if (status.state === "failed")
      label = `Encoding failed${status.message ? `: ${status.message}` : ""}`;
    else if (status.rawStatus)
      label = `Transcoding: ${status.rawStatus}${
        status.progress !== null ? ` (${status.progress}%)` : "…"
      }`;
  }

  return (
    <div
      className="title-transcode-status-library"
      data-testid="title-transcode-status"
      aria-live="polite"
    >
      {label}
    </div>
  );
};

const CreatorContent: React.FC = () => {
  const [isVerified, setIsVerified] = useState(
    getCookie("userIsVerified") === "true"
  );
  const userToken: string | null = getCookie("userToken");

  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [searchValue, setSearchValue] = useState("");
  const [formatFilter, setFormatFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [uploadedTitles, setUploadedTitles] = useState<UploadedTitle[]>([]);

  const [formatOptions, setFormatOptions] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refreshLibrary = useCallback(() => setRefreshVersion((value) => value + 1), []);
  const [removalTitle, setRemovalTitle] = useState<UploadedTitle | null>(null);
  const [requestingRemoval, setRequestingRemoval] = useState(false);

  type UploadedTitle = {
    assetId: string;
    title: string;
    thumbnail: string;
    status: string;
    category: string;
    genre: string;
    duration: string;
    format: string;
    file_extension: string;
    rejectionReason: string;
    videoId: string;
    videoStatus: string;
  };

  useEffect(() => {
    let active = true;
    getAssetsByUserToken()
      .then((data) => {
        if (!active) return;
        setErrorMessage("");
        const uploaded_titles: UploadedTitle[] = data.map((item: any) => ({
          assetId: item.id,
          title: item.title ? item.title : "N/A",
          thumbnail: item.imageLib?.length > 0 ? item.imageLib[0].fileName : "",
          status: item.status,
          category: item.category ? item.category : "N/A",
          genre: item.genre ? item.genre : "N/A",
          duration:
            item.videoLib?.length > 0
              ? `${
                  item.videoLib[0].duration < 60
                    ? `${item.videoLib[0].duration} sec`
                    : item.videoLib[0].duration < 3600
                    ? `${Math.floor(item.videoLib[0].duration / 60)} min`
                    : `${Math.floor(item.videoLib[0].duration / 3600)} hour${
                        Math.floor(item.videoLib[0].duration / 3600) > 1
                          ? "s"
                          : ""
                      } ${Math.floor(
                        (item.videoLib[0].duration % 3600) / 60
                      )} min`
                }`
              : "N/A",
          format: item.format ? item.format : "N/A",
          file_extension:
            item.videoLib?.length > 0
              ? item.videoLib[0].fileName.substring(
                  item.videoLib[0].fileName.indexOf(".")
                )
              : "N/A",
          rejectionReason: String(
            item.rejectionReason ?? item.rejection_reason ?? ""
          ).trim(),
          videoId: String(item.videoId ?? getMainVideo(item)?.id ?? ""),
          videoStatus: String(
            item.videoStatus ?? getMainVideo(item)?.status ?? ""
          ),
        }));
        // Reverse the array to show newest assets first
        setUploadedTitles(uploaded_titles.reverse());
        // Extract unique values
        setFormatOptions([
          ...new Set(
            uploaded_titles.map((t) => t.format).filter((v) => v && v !== "N/A")
          ),
        ]);
        setCategoryOptions([
          ...new Set(
            uploaded_titles
              .map((t) => t.category)
              .filter((v) => v && v !== "N/A")
          ),
        ]);
        setStatusOptions([
          ...new Set(uploaded_titles.map((t) => t.status).filter(Boolean)),
        ]);
      })
      .catch((error) => {
        if (active) setErrorMessage(parseApiError(error, "Could not load your library."));
        console.error("Error fetching assets:", error);
      });
    return () => { active = false; };
  }, [refreshVersion]);

  const filteredTitles = uploadedTitles.filter((item) => {
    const matchesSearch = item.title
      .toLowerCase()
      .includes(searchValue.toLowerCase());
    const matchesFormat = !formatFilter || item.format === formatFilter;
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesStatus = !statusFilter || item.status === statusFilter;
    return matchesSearch && matchesFormat && matchesCategory && matchesStatus;
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTitles = filteredTitles.slice(indexOfFirstItem, indexOfLastItem);

  const totalItems = filteredTitles.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
      setOpenMenuIdx(null);
    }
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, formatFilter, categoryFilter, statusFilter]);

  // In CreatorContent.jsx
  const handleEdit = (assetId: string, status: string) => {
    navigate("/home/upload", { state: { assetId, status } });
  };

  const handleRemoveAsset = async (assetId: string) => {
    if (!userToken) {
      console.error("No user token found. User might not be authenticated.");
      return;
    }
    try {
      setErrorMessage("");
      await removeAsset(assetId);
      setUploadedTitles((prev) => prev.filter((item) => item.assetId !== assetId));
      // Close the menu after deletion
      setOpenMenuIdx(null);
      //  notify the user
      setSuccessMessage("Title deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(parseApiError(error, "Could not delete this title."));
      console.error("Error deleting asset:", error);
    }
  };

  const requestRemoval = async () => {
    if (!removalTitle || requestingRemoval) return;
    setRequestingRemoval(true);
    setErrorMessage("");
    try {
      await apiClient.post(`/api/Asset/${removalTitle.assetId}/request-removal`);
      setSuccessMessage("Removal request sent for review.");
      setRemovalTitle(null);
      setOpenMenuIdx(null);
    } catch (error) {
      setErrorMessage(parseApiError(error, "Could not request removal."));
    } finally {
      setRequestingRemoval(false);
    }
  };

  const handlePublish = async (assetId: string) => {
    if (!userToken) {
      console.error("No user token found. User might not be authenticated.");
      return;
    }

    try {
      // Fetch the asset details to get all the metadata and file IDs
      const asset = await getAssetById(assetId);

      // Get video IDs
      const mainVideoObj = asset.videos?.find(
        (v: any) => v.role === "MainVideo"
      );
      const trailerObj = asset.videos?.find((v: any) => v.role === "Trailer");
      const videoIds = [
        ...(mainVideoObj?.id ? [mainVideoObj.id] : []),
        ...(trailerObj?.id ? [trailerObj.id] : []),
        ...(asset.videoLib?.map((v: any) => v.id) || []),
      ].filter(Boolean);

      // Get image IDs
      const heroImageObj = asset.images?.find(
        (i: any) => i.role === "HeroImage"
      );
      const thumbnailObj = asset.images?.find(
        (i: any) => i.role === "Thumbnail"
      );
      const imageIds = [
        ...(heroImageObj?.id ? [heroImageObj.id] : []),
        ...(thumbnailObj?.id ? [thumbnailObj.id] : []),
        ...(asset.imageLib?.map((i: any) => i.id) || []),
      ].filter(Boolean);

      // Get subtitle IDs
      const subtitleIds = asset.subtitleLib?.map((s: any) => s.id) || [];

      const userId = getCookie("userId");
      if (!userId) {
        alert("User ID not found. Please log in again.");
        return;
      }

      const payload = {
        title: asset.title || "",
        language: asset.language || "",
        category: asset.category || "",
        genre: asset.genre || "",
        description: asset.description || "",
        format: asset.format || "",
        ageRating: asset.ageRating || "",
        comfortLevel: asset.comfortLevel || "",
        copyrightOwner: asset.copyrightOwner || "",
        publisher: asset.publisher || "",
        releaseDate: asset.releaseDate
          ? new Date(asset.releaseDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        festivalParticipations: asset.festivalParticipations || "",
        awards: asset.awards || "",
        userId: userId,
        assetId: parseInt(assetId) || 0,
        videoIds: videoIds,
        imageIds: imageIds,
        subtitleIds: subtitleIds,
      };

      await assetApi.postApiAssetSubmit(payload as any);

      // Update the local state to reflect the status change
      setUploadedTitles((prev) =>
        prev.map((item) =>
          item.assetId === assetId
            ? { ...item, status: "UnderReview", rejectionReason: "" }
            : item
        )
      );

      setSuccessMessage("Title submitted for publishing successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error publishing title:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to publish title";

      // Check if it's the specific validation error
      if (errorMessage.includes("All required fields must be completed")) {
        setErrorMessage(
          "Please complete all required fields and upload all required files before publishing."
        );
        setTimeout(() => setErrorMessage(""), 5000);
      } else {
        alert(errorMessage);
      }
    }
  };

  // Three dots button functionality
  const [openMenuIdx, setOpenMenuIdx] = useState<number | null>(null);
  const menuRefs = useRef<(HTMLDivElement | null)[]>([]);

  return uploadedTitles.length > 0 ? (
    <div className="library-creator-content">
      <div ref={topRef}></div>
      {successMessage && (
        <div className="password-success-box">{successMessage}</div>
      )}
      {errorMessage && <div role="alert" className="password-error-box">{errorMessage}</div>}
      {removalTitle && (
        <div className="uploadflow-modal-overlay">
          <div className="uploadflow-modal" role="dialog" aria-modal="true" aria-labelledby="removal-title">
            <h2 id="removal-title">Request to remove {removalTitle.title}?</h2>
            <p>Your request will be sent for review.</p>
            <button type="button" disabled={requestingRemoval} onClick={() => setRemovalTitle(null)}>Cancel</button>
            <button type="button" disabled={requestingRemoval} onClick={requestRemoval}>{requestingRemoval ? "Sending…" : "Request removal"}</button>
          </div>
        </div>
      )}
      <div className="main-library">
        <div className="top-container-library">
          <div className="left-top-container-library">
            <div className="title-top-container-library">Library</div>
            <div className="subtitle-top-container-library">
              Manage your uploaded titles
            </div>
          </div>
          <div className="right-top-container-library">
            <button
              className="library-top-container-button"
              onClick={() => navigate("/Home/upload")}
            >
              Upload new
            </button>
          </div>
        </div>
        <div className="search-container-library">
          <div className="left-search-container-library">
            <div className="search-input-wrapper">
              <button
                type="button"
                className="search-icon-button"
                aria-label="Search"
              >
                <img
                  src="/images/icons/search.svg"
                  alt="Search"
                  width="18"
                  height="18"
                  style={{ display: "block" }}
                />
              </button>
              <input
                type="text"
                placeholder="Search titles"
                className="search-input-library"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </div>

          <div className="right-search-container-library">
            <select
              className="search-dropdown-library"
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
            >
              <option value="">Content format</option>
              {formatOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              className="search-dropdown-library"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">Category</option>
              {categoryOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              className="search-dropdown-library"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Status</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option
                    .replace(/_/g, " ")
                    .replace(/underreview/i, "Under Review")}
                </option>
              ))}
            </select>
          </div>
        </div>
        {currentTitles.length === 0 ? ( // Display no titles message if search returns nothing
          <div className="no-titles-message-library">
            <div className="title-no-titles-library">No matching results</div>
            <div className="subtitle-no-titles-library">
              Try different search terms or filters
            </div>
          </div>
        ) : (
          <div className="title-list-library">
            {currentTitles.map((item, idx) => (
              <div className="title-list-row" key={item.assetId}>
                <div className="left-title-list-library">
                  <ImageWithSpinner
                    src={
                      item.thumbnail
                        ? `${getApiOrigin()}/api/Image/stream/${item.thumbnail}`
                        : "/images/no_thumbnail.svg"
                    }
                    alt="thumbnail"
                    className="thumbnail-img-library"
                  />
                  <div className="title-details-container-library">
                    <div>
                      <span
                        className="title-details-status-library"
                        id={`${item.status}-status-library`}
                      >
                        {item.status
                          .replace(/_/g, " ")
                          .replace(/underreview/i, "under review")}
                      </span>
                    </div>
                    <div className="title-details-title-library">
                      {item.title}
                    </div>
                    {item.status === "Rejected" && (
                      <div
                        className="rejection-reason-library"
                        role="alert"
                        data-testid="rejection-reason"
                      >
                        <strong>Rejected:</strong>{" "}
                        {item.rejectionReason ||
                          "No reason was provided by the curator."}
                      </div>
                    )}
                    {isTitleTranscoding(item) && (
                      <TitleTranscodeStatus videoId={item.videoId} onCompleted={refreshLibrary} />
                    )}
                    <div className="title-details-category-genre-duration-library">
                      <span>{item.category}</span>
                      <span>{item.genre}</span>
                      <span className="title-details-duration-library">
                        {item.duration}
                      </span>
                    </div>
                    <div className="title-details-file-format-library">
                      <span className="title-details-file-extension-library">
                        {item.file_extension}
                      </span>
                      <div className="title-details-format-library">
                        {item.format}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="right-title-list-library">
                  <div className="btns-right-title-list-library">
                    <Tooltip id="my-tooltip" />
                    {item.status === "Published" ? (
                      <button type="button" id="analytics-button-library" onClick={() => navigate("/home/analytics")}>
                        View analytics
                      </button>
                    ) : (
                      ""
                    )}
                    {item.status === "Draft" || item.status === "Rejected" ? (
                      <button
                        type="button"
                        id="publish-button-library"
                        onClick={() => handlePublish(item.assetId)}
                      >
                        {item.status === "Rejected" ? "Resubmit" : "Publish"}
                      </button>
                    ) : (
                      ""
                    )}
                    {item.status === "Published" ||
                    item.status === "Draft" ||
                    item.status === "Rejected" ? (
                      <button
                        className="active-three-dots-button-library"
                        onClick={() =>
                          setOpenMenuIdx(openMenuIdx === idx ? null : idx)
                        }
                      >
                        <img
                          src="/images/icons/three-dots-button.svg"
                          alt="dots"
                        />
                      </button>
                    ) : (
                      <img
                        src="/images/icons/three-dots-button.svg"
                        alt="dots"
                        className="disabled-three-dots-button-library"
                        data-tooltip-id="my-tooltip"
                        data-tooltip-place="top"
                        data-tooltip-position-strategy="absolute"
                        data-tooltip-content={
                          item.status === "Encoding"
                            ? "Can't make any changes while encoding video"
                            : "Can't make any changes while under review"
                        }
                      />
                    )}
                  </div>
                  <div
                    className={
                      "options-right-title-list-library" +
                      (openMenuIdx === idx ? " show" : " hide")
                    }
                    ref={(el) => {
                      menuRefs.current[idx] = el;
                    }}
                  >
                    <button
                      onClick={() => handleEdit(item.assetId, item.status)}
                    >
                      {item.status === "Rejected" ? "Edit title" : "Edit"}
                    </button>

                    {item.status === "Rejected" && (
                      <button onClick={() => handlePublish(item.assetId)}>
                        Resubmit
                      </button>
                    )}

                    {item.status === "Draft" || item.status === "Rejected" ? (
                      <button onClick={() => handleRemoveAsset(item.assetId)}>
                        {item.status === "Rejected" ? "Delete" : "Remove"}
                      </button>
                    ) : (
                      <button onClick={() => setRemovalTitle(item)}>Request to remove</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {totalItems > itemsPerPage && (
          <ReactPaginate
            pageCount={totalPages}
            forcePage={currentPage - 1}
            onPageChange={(event) => setCurrentPage(event.selected + 1)}
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            breakClassName="break-page-library"
            containerClassName="pagination-controls-library"
            pageClassName="page-library"
            pageLinkClassName="page-library-link"
            activeClassName="active-page-library"
            activeLinkClassName="active-page-library-link"
            previousClassName="prev-next-button-library"
            previousLinkClassName="prev-next-button-library-link"
            nextClassName="prev-next-button-library"
            nextLinkClassName="prev-next-button-library-link"
            disabledClassName="disabled"
          />
        )}
      </div>
    </div>
  ) : (
    <div className="library-no-titles-creator-content">
      {errorMessage && <div role="alert" className="password-error-box">{errorMessage}</div>}
      <div className="main-library-no-titles">
        {!isVerified && (
          <EmailVerificationBanner
            className="alert-div-library"
            onVerified={() => setIsVerified(true)}
          />
        )}

        <div className="mid-container-no-titles-library">
          <h1 className="no-titles">No uploaded titles yet.</h1>
          <p className="upload-content">
            Start uploading your XR content to Poetics Stream.
          </p>
          <button
            type="button"
            className="upload-btn"
            onClick={() => navigate("/home/upload")}
            disabled={!isVerified}
          >
            <span className="upload-btn-label">Upload your first title</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatorContent;
